<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Absolvierte Einheiten durch externe Trainer
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Einheiten pro Monat</h5>

        <Menu/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            @search="getUnitsList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getUnitsList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto pt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiternr</th>
              <th>Extern/Intern</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Rechte</th>
              <th class="text-end">Einheiten erledigt</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="listData in unitsList.data" :key="listData.coachId">
              <td>{{ listData.employeeNumber }}</td>
              <td>{{ $t(`user_engagement_type.${listData.engagementType}`) }}</td>
              <td>{{ listData.firstName }}</td>
              <td>{{ listData.lastName }}</td>
              <td>{{ $t(`user_access_right.${listData.userAccessRights}`) }}</td>
              <td class="text-end">{{ listData.sumUnits }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="unitsList?.pagination?.total_pages > 1"
            :totalPages="unitsList?.pagination?.total_pages"
            :currentPage="unitsList?.pagination?.current_page"
            :totalItems="unitsList?.pagination?.total_items"
            :perPage="unitsList?.pagination?.items_per_page"
            @pagechanged="getUnitsList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsService from '@/api/Analytics';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getExternalCoachDropdown } from '@/shared/dropdowns';
import { downloadFile } from '@/shared/functions';
import Menu from '@/views/App/Analytics/UnitsDoneByExternalCoaches/Menu.vue';
import Search from '@/views/App/Analytics/UnitsDoneByExternalCoaches/Week/Search.vue';
import { onMounted, ref } from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const currentDate = new Date();
const currentDay  = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

const monday = new Date(currentDate);
monday.setDate(currentDate.getDate() - (currentDay === 0 ? 6 : currentDay - 1));

const sunday = new Date(monday);
sunday.setDate(monday.getDate() + 6);

const formatDate = (date) => date.toLocaleDateString('de-DE'); // Formats as 'dd.mm.yyyy'

const filters = ref({
  coachId: '',
  startDate: formatDate(monday),
  endDate: formatDate(sunday),
});

const unitsList     = ref([]);
const coachDropdown = ref([]);
const isSearching   = ref(false);
const isLoaded      = ref(false);
const isExcelLoaded = ref(false);

async function getUnitsList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = unitsList.value?.pagination?.items_per_page;

  try {
    const { data } = await analyticsService.getUnitsDoneByExternalCoachesPerWeek({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    unitsList.value   = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getExcelExport() {
  await downloadFile('/analytics/units-done-by-external-coaches/week-excel-export',
      undefined,
      {
        ...filters.value,
      },
      false,
      'GET',
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getExternalCoachDropdown(coachDropdown),
    getUnitsList(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>